// Copyright (c) 2020 WiseTime. All rights reserved.

import { merge, Observable, of } from "rxjs"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { Action } from "redux"
import * as TimelineActions from "../actions"
import * as Slice from "../../user-context/slice"
import {
  TimelineUpdate,
  WatchTimelineUpdatesRequest
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { debug } from "app/logging"
import { ClientReadableStream, RpcError, StatusCode } from "grpc-web"
import { RetryPolicy, withExponentialDelay } from "grpc-web-rx"
import { RootState, store } from "../../../app/store"
import { refreshTokenExchange } from "../../../app/rpc/retry"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { hideSnackBar, showSnackBar } from "../../snackbar/actions"

const toastId = "watch-timeline-failed"

type WatchTimelineUpdatesRpc = () => Observable<TimelineUpdate>

let currentStream: ClientReadableStream<TimelineUpdate> | undefined = undefined
const getStream = (request: WatchTimelineUpdatesRequest): ClientReadableStream<TimelineUpdate> => {
  debug("Request WatchTimelineUpdates stream")
  cancelPreviousStream()
  currentStream = timelineClient.watchTimelineUpdates(request) as ClientReadableStream<TimelineUpdate>
  currentStream.on("end", () => {
    debug("WatchTimelineUpdates stream finished, resubscribing...")
    // resubscribe to WatchTimelineUpdates stream
    store.dispatch(TimelineActions.watchTimelineUpdates())
  })
  return currentStream
}

const cancelPreviousStream = (): void => {
  if (currentStream) {
    debug("Cancelling previous WatchTimelineUpdates stream")
    currentStream.cancel()
  }
}

export const watchTimelineUpdates = (watchTimelineUpdatesRpc: WatchTimelineUpdatesRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(action =>
        TimelineActions.watchTimelineUpdates.match(action) ||
        Slice.setUserDetails.match(action)
      ),
      mergeMap(watchTimelineUpdatesRpc),
      withLatestFrom(state$),
      mergeMap(([update, state]) => handleUpdate(update, state)),
      catchError(handleError),
    )

let retryAttempt = 0
const handleUpdate = (update: TimelineUpdate, state: RootState): Observable<Action> => {
  const firstUpdateAfterFailure = retryAttempt > 0
  if (firstUpdateAfterFailure) {
    retryAttempt = 0
    return of(
      TimelineActions.getTimelineData(), // Fetch timeline for the active date.
      hideSnackBar(toastId) // Hide potential warning message re failed timeline watch.
    )
  }
  // Request data only for cached dates.
  // Exceptions:
  // currentDay might receive recorded time and should be always up-to-date
  // activeDate without timeline data might receive manual time
  const cachedDates = [
    state.timeline.currentDay.toString(),
    state.timeline.activeDate.toString(),
    ...Object.keys(state.timeline.timeRowsByDateIndex),
    ...Object.keys(state.timeline.timeGroupsByDateIndex),
    ...Object.keys(state.timeline.trashedTimeRowsByDateIndex),
  ]
  const datesToUpdate = update.getDatesWithUpdatesList().filter(date => cachedDates.includes(date))
  return merge(
    datesToUpdate.map(date => TimelineActions.getTimelineData(Number(date)))
  )
}

const handleError = (error: unknown): Observable<Action> => {
  if (isGrpcError(error)) {
    // the stream is cancelled when the deadline is exceeded
    if (error.code === StatusCode.DEADLINE_EXCEEDED) {
      debug("WatchTimelineUpdates has exceeded the grpc request deadline, resubscribing...")
      return of(TimelineActions.watchTimelineUpdates())
    }
  }
  return of(errorMessageAction())
}

const errorMessageAction = (): Action => {
  return showSnackBar({
    message: t({ id: "msg_watch_timeline_failed", message: "Refresh required. Please reload this page to see new time rows." }),
    messageType: "INFO",
    options: {
      toastId,
      autoClose: false,
    }
  })
}

const watchTimelineRetryAction = (error: RpcError) => {
  if (isGrpcError(error) && error.code === StatusCode.UNAUTHENTICATED) {
    return refreshTokenExchange()
  }
  return of(++retryAttempt).pipe(
    // show error after 5 consecutive failures
    map(attempt => {
      !(attempt % 5) && store.dispatch(errorMessageAction())
    }),
  )
}

/**
 * Policy to retry a request multiple times, with exponential delay between each call.
 */
export const watchTimelineRetryPolicy = (): RetryPolicy =>
  ({
    shouldRetry: (): boolean => true,
    maxRetries: Infinity,
    beforeRetry: withExponentialDelay<void>(5_000, 60_000)(watchTimelineRetryAction)
  })

const watchTimelineUpdatesRpc = (): Observable<TimelineUpdate> =>
  grpc(() => getStream(new WatchTimelineUpdatesRequest()), watchTimelineRetryPolicy())

export default watchTimelineUpdates(watchTimelineUpdatesRpc)
